import { CatalogAdoption } from './CatalogAdoption';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Checkbox,
  Divider,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { client } from '@frontend/features/common/services';
import { countries } from '@frontend/features/common/constants/countries';
import { CONFIG } from '@frontend/config';
import { catalogAdminKey, catalogAdminPath } from './queries';
import {
  DiscoveredHotel,
  DiscoveryJob,
  DiscoveryPage,
  DiscoveryRequest,
  discoveryCsv,
  discoveryPending,
} from './discovery';
const root = 'hotel-catalog-admin/discoveries';
interface Account {
  connector_account_id: string;
  display_name: string;
  status: string;
  access_mode: string;
  access_codes: string[];
}
interface Connection {
  key: string;
  label: string;
  connector: 'hotelbeds' | 'travelgate';
  account: string;
  access?: string;
}
export function CatalogDiscovery({ active = true }: { active?: boolean }) {
  const { t, i18n } = useTranslation();
  const cache = useQueryClient();
  const pendingKey = `catalog-discovery-pending:${CONFIG.AGENCY_ID}`;
  const [pending, setPending] = useState<DiscoveryRequest | null>(() => {
    try {
      return JSON.parse(sessionStorage.getItem(pendingKey) || 'null');
    } catch {
      return null;
    }
  });
  const [storageError, setStorageError] = useState(false);
  const [connection, setConnection] = useState(''),
    [country, setCountry] = useState(''),
    [destination, setDestination] = useState('');
  const [jobId, setJobId] = useState(''),
    [offset, setOffset] = useState(0);
  const saving = useRef(false);
  const accounts = useQuery({
    queryKey: [...catalogAdminKey, 'discovery-connections'],
    enabled: active,
    retry: false,
    queryFn: async () => {
      const groups = await Promise.all(
        (['hotelbeds', 'travelgate'] as const).map(async (connector) => {
          const result = (
            await client.get<{ data: Account[] }>('hotel-catalog-admin/import-accounts', { params: { connector } })
          ).data;
          return result.data
            .filter(
              (a) => a.status === 'active' && a.access_mode === (connector === 'hotelbeds' ? 'direct' : 'include'),
            )
            .flatMap<Connection>((a) =>
              connector === 'hotelbeds'
                ? [
                  {
                    key: a.connector_account_id,
                    label: `${a.display_name} · Hotelbeds`,
                    connector,
                    account: a.connector_account_id,
                  },
                ]
                : a.access_codes.map((access) => ({
                  key: JSON.stringify([a.connector_account_id, access]),
                  label: `${a.display_name} · ${access} · Travelgate`,
                  connector,
                  account: a.connector_account_id,
                  access,
                })),);
        }),
      );
      return groups.flat() as Connection[];
    },
  });
  const selected = accounts.data?.find((c) => c.key === connection);
  const names = useMemo(() => {
    try {
      return new Intl.DisplayNames([i18n.language || 'es'], { type: 'region' });
    } catch {
      return null;
    }
  }, [i18n.language]);
  const countryOptions = useMemo(
    () =>
      countries
        .map((c) => ({ code: c.code, label: names?.of(c.code) || c.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [names],
  );
  const history = useQuery({
    queryKey: [...catalogAdminKey, 'discoveries', offset],
    enabled: active,
    retry: false,
    queryFn: async () => (await client.get<DiscoveryPage<DiscoveryJob>>(root, { params: { limit: 25, offset } })).data,
  });
  const create = useMutation({
    retry: false,
    mutationFn: async (body: DiscoveryRequest) => (await client.post<{ data: DiscoveryJob }>(root, body)).data.data,
    onSuccess: (data) => {
      try {
        sessionStorage.removeItem(pendingKey);
      } catch {
        /* The existing UUID is safe to replay. */
      }
      setPending(null);
      setJobId(data.job_id);
      setOffset(0);
      cache.invalidateQueries([...catalogAdminKey, 'discoveries']);
    },
    onSettled: () => {
      saving.current = false;
    },
  });
  const start = () => {
    if (saving.current || (!pending && (!selected || !/^[A-Z]{2}$/.test(country)))) return;
    const body = pending || {
      request_id: crypto.randomUUID(),
      connector_account_id: selected!.account,
      country_code: country,
      ...(selected!.access ? { access_code: selected!.access } : {}),
      ...(destination.trim() ? { destination_code: destination.trim() } : {}),
    };
    // Persist the UUID before dispatch so an uncertain response can be retried safely after reload.
    try {
      sessionStorage.setItem(pendingKey, JSON.stringify(body));
      setStorageError(false);
    } catch {
      setStorageError(true);
      return;
    }
    setPending(body);
    saving.current = true;
    create.mutate(body);
  };
  return (
    <Stack spacing={3} sx={{ '& input, & .MuiSelect-select': { fontSize: '16px !important' } }}>
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
        <Typography variant="h5">{t('hotelDiscovery.title')}</Typography>
        <Typography color="text.secondary" sx={{ mt: 1, mb: 3 }}>
          {t('hotelDiscovery.help')}
        </Typography>
        <Stack spacing={2} direction={{ xs: 'column', md: 'row' }} alignItems="flex-start">
          <TextField
            select
            fullWidth
            label={t('hotelDiscovery.connection')}
            value={connection}
            disabled={!!pending || create.isLoading}
            onChange={(e) => {
              setConnection(e.target.value);
              setDestination('');
            }}
          >
            {(accounts.data || []).map((c) => (
              <MenuItem value={c.key} key={c.key}>
                {c.label}
              </MenuItem>
            ))}
          </TextField>
          <Autocomplete
            fullWidth
            options={countryOptions}
            value={countryOptions.find((c) => c.code === country) || null}
            disabled={!!pending || create.isLoading}
            isOptionEqualToValue={(a, b) => a.code === b.code}
            onChange={(_, c) => setCountry(c?.code || '')}
            renderInput={(params) => <TextField {...params} label={t('hotelDiscovery.country')} />}
          />
          <TextField
            fullWidth
            label={t('hotelDiscovery.destination')}
            helperText={t('hotelDiscovery.destinationHelp')}
            value={destination}
            inputProps={{ maxLength: 100 }}
            disabled={!!pending || create.isLoading}
            onChange={(e) => setDestination(e.target.value)}
          />
        </Stack>
        {accounts.isError && (
          <Alert
            severity="error"
            action={<Button onClick={() => accounts.refetch()}>{t('hotelCatalogAdmin.reload')}</Button>}
          >
            {t('hotelCatalogAdmin.loadError')}
          </Alert>
        )}
        {accounts.data?.length === 0 && <Alert severity="info">{t('hotelDiscovery.noConnections')}</Alert>}
        {storageError && <Alert severity="error">{t('hotelDiscovery.storageError')}</Alert>}
        {pending && (
          <Alert severity={create.isError ? 'warning' : 'info'} sx={{ mt: 2 }}>
            {t('hotelDiscovery.pending', { country: pending.country_code, account: pending.connector_account_id })}
          </Alert>
        )}
        {pending && [400, 404, 409, 422].includes((create.error as any)?.response?.status) && (
          <Button
            onClick={() => {
              try {
                sessionStorage.removeItem(pendingKey);
                setPending(null);
                create.reset();
              } catch {
                setStorageError(true);
              }
            }}
          >
            {t('hotelDiscovery.correct')}
          </Button>
        )}
        <Button
          variant="contained"
          sx={{ mt: 2 }}
          disabled={create.isLoading || (!pending && (!selected || !country))}
          onClick={start}
        >
          {t(pending ? 'hotelDiscovery.recover' : 'hotelDiscovery.start')}
        </Button>
      </Paper>
      {jobId && <DiscoveryResults key={jobId} jobId={jobId} active={active} onClose={() => setJobId('')} />}
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{t('hotelDiscovery.history')}</Typography>
          <Button onClick={() => history.refetch()} disabled={history.isFetching}>
            {t('hotelCatalogAdmin.reload')}
          </Button>
        </Stack>
        {history.isError && <Alert severity="error">{t('hotelCatalogAdmin.loadError')}</Alert>}
        {history.isLoading && <LinearProgress />}
        {history.data?.data.length === 0 && (
          <Typography color="text.secondary">{t('hotelDiscovery.noRuns')}</Typography>
        )}
        <Stack spacing={1} sx={{ mt: 2 }}>
          {history.data?.data.map((j) => (
            <Paper variant="outlined" key={j.job_id} sx={{ p: 2 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={600}>
                    {names?.of(j.country_code) || j.country_code} · {j.account_name}
                    {j.access_code ? ` · ${j.access_code}` : ''}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(j.created_at).toLocaleString(i18n.language)} ·{' '}
                    {t('hotelDiscovery.count', { count: j.hotels_processed })}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={t(`hotelDiscovery.status.${j.status}`)}
                  color={j.status === 'completed' ? 'success' : j.status === 'terminal_failed' ? 'error' : 'default'}
                />
                <Button onClick={() => setJobId(j.job_id)}>{t('hotelDiscovery.view')}</Button>
              </Stack>
            </Paper>
          ))}
        </Stack>
        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <Button disabled={offset === 0 || history.isFetching} onClick={() => setOffset((v) => Math.max(0, v - 25))}>
            {t('hotelCatalogAdmin.previous')}
          </Button>
          <Button
            disabled={!history.data?.meta.has_more || history.isFetching || offset >= 10000000}
            onClick={() => setOffset((v) => v + 25)}
          >
            {t('hotelCatalogAdmin.next')}
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
}
export function DiscoveryResults({ jobId, active, onClose }: { jobId: string; active: boolean; onClose(): void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [offset, setOffset] = useState(0),
    [q, setQ] = useState(''),
    [applied, setApplied] = useState(''),
    [linked, setLinked] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [exporting, setExporting] = useState(false),
    [exportCount, setExportCount] = useState(0),
    [exportError, setExportError] = useState(false);
  const exportController = useRef<AbortController | null>(null);
  useEffect(
    () => () => {
      exportController.current?.abort();
    },
    [],
  );
  const job = useQuery({
    queryKey: [...catalogAdminKey, 'discovery', jobId],
    enabled: active,
    retry: false,
    queryFn: async () => (await client.get<{ data: DiscoveryJob }>(`${root}/${encodeURIComponent(jobId)}`)).data.data,
    refetchInterval: (d) => (discoveryPending(d?.status) && active ? 5000 : false),
    refetchIntervalInBackground: false,
  });
  const hotels = useQuery({
    queryKey: [...catalogAdminKey, 'discovered-hotels', jobId, offset, applied, linked, job.data?.pages_processed],
    enabled: active,
    retry: false,
    queryFn: async () =>
      (
        await client.get<DiscoveryPage<DiscoveredHotel>>(`${root}/${encodeURIComponent(jobId)}/hotels`, {
          params: { limit: 25, offset, ...(applied ? { q: applied } : {}), ...(linked ? { linked } : {}) },
        })
      ).data,
  });
  const exportAll = async () => {
    if (exporting || job.data?.status !== 'completed') return;
    const controller = new AbortController();
    exportController.current = controller;
    setExporting(true);
    setExportError(false);
    setExportCount(0);
    try {
      const csv = await discoveryCsv(
        async (n) =>
          (
            await client.get<DiscoveryPage<DiscoveredHotel>>(`${root}/${encodeURIComponent(jobId)}/hotels`, {
              params: { limit: 100, offset: n },
              signal: controller.signal,
            })
          ).data,
        () => controller.signal.aborted,
        setExportCount,
      );
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `hotels-${jobId}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      if (!controller.signal.aborted) setExportError(true);
    } finally {
      if (exportController.current === controller) setExporting(false);
    }
  };
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" spacing={2} justifyContent="space-between">
        <Typography variant="h5">{t('hotelDiscovery.results')}</Typography>
        <Button onClick={onClose}>{t('hotelDiscovery.close')}</Button>
      </Stack>
      {job.isError && (
        <Alert severity="error" action={<Button onClick={() => job.refetch()}>{t('hotelCatalogAdmin.reload')}</Button>}>
          {t('hotelCatalogAdmin.loadError')}
        </Alert>
      )}
      {job.data && (
        <>
          <Typography sx={{ mt: 1 }}>
            {job.data.country_code} · {job.data.account_name}
            {job.data.access_code ? ` · ${job.data.access_code}` : ''}
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={2} sx={{ my: 2 }}>
            <Chip label={t(`hotelDiscovery.status.${job.data.status}`)} />
            <Typography>
              {t('hotelDiscovery.progress', { pages: job.data.pages_processed, count: job.data.hotels_processed })}
            </Typography>
          </Stack>
          {discoveryPending(job.data.status) && (
            <>
              <LinearProgress />
              <Typography variant="body2" sx={{ my: 1 }}>
                {t('hotelDiscovery.running')}
              </Typography>
            </>
          )}
          {job.data.status === 'terminal_failed' && (
            <Alert severity="warning">
              {t('hotelDiscovery.failed')} {job.data.last_error_code}
            </Alert>
          )}
        </>
      )}
      <Alert severity="info" sx={{ my: 2 }}>
        {t('hotelDiscovery.resultsHelp')}
      </Alert>
      <CatalogAdoption
        discoveryId={jobId}
        complete={job.data?.status === 'completed'}
        active={active}
        selected={selectedIds}
        onSelect={setSelectedIds}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ my: 2 }}>
          <TextField
            fullWidth
            label={t('hotelDiscovery.search')}
            value={q}
            inputProps={{ maxLength: 128 }}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setApplied(q.trim());
                setOffset(0);
              }
            }}
          />
          <TextField
            select
            fullWidth
            label={t('hotelDiscovery.link')}
            value={linked}
            onChange={(e) => {
              setLinked(e.target.value);
              setOffset(0);
            }}
          >
            <MenuItem value="">{t('hotelDiscovery.all')}</MenuItem>
            <MenuItem value="false">{t('hotelDiscovery.unlinked')}</MenuItem>
            <MenuItem value="true">{t('hotelDiscovery.linked')}</MenuItem>
          </TextField>
          <Button
            onClick={() => {
              setApplied(q.trim());
              setOffset(0);
              hotels.refetch();
            }}
          >
            {t('hotelCatalogAdmin.filter')}
          </Button>
        </Stack>
        <Stack direction="row" gap={2} flexWrap="wrap" sx={{ mb: 2 }}>
          <Button variant="outlined" disabled={job.data?.status !== 'completed' || exporting} onClick={exportAll}>
            {t('hotelDiscovery.export')}
          </Button>
          {exporting && (
            <>
              <Typography>{t('hotelDiscovery.exportProgress', { count: exportCount })}</Typography>
              <Button
                onClick={() => {
                  exportController.current?.abort();
                  setExporting(false);
                }}
              >
                {t('hotelDiscovery.cancel')}
              </Button>
            </>
          )}
        </Stack>
        {exportError && <Alert severity="error">{t('hotelDiscovery.exportError')}</Alert>}

        {hotels.isError && (
          <Alert
            severity="error"
            action={<Button onClick={() => hotels.refetch()}>{t('hotelCatalogAdmin.reload')}</Button>}
          >
            {t('hotelCatalogAdmin.loadError')}
          </Alert>
        )}
        {hotels.isFetching && <LinearProgress />}
        {!hotels.isFetching && hotels.data?.data.length === 0 && <Typography>{t('hotelDiscovery.empty')}</Typography>}
        <Stack divider={<Divider />}>
          {hotels.data?.data.map((h) => (
            <Stack key={h.supplier_hotel_id} direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ py: 2 }}>
              <Checkbox
                checked={selectedIds.includes(h.supplier_hotel_id)}
                disabled={job.data?.status !== 'completed'}
                inputProps={{ 'aria-label': t('hotelAdoption.selectHotel', { name: h.display_name || h.hotel_code }) }}
                onChange={(_, checked) =>
                  setSelectedIds((ids) =>
                    checked
                      ? [...new Set([...ids, h.supplier_hotel_id])]
                      : ids.filter((id) => id !== h.supplier_hotel_id),)
                }
              />
              <Box sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
                <Typography fontWeight={600}>{h.display_name || h.hotel_code}</Typography>
                <Typography color="text.secondary">
                  {[h.locality, h.country_code].filter(Boolean).join(' · ')}
                </Typography>
                <Typography variant="body2">{h.address}</Typography>
                <Typography variant="caption">
                  {t('hotelDiscovery.code')}: {h.hotel_code}
                  {h.native_hotel_code && h.native_hotel_code !== h.hotel_code ? ` · ${h.native_hotel_code}` : ''}
                  {h.fastx_codes.length ? ` · FastX: ${h.fastx_codes.join(', ')}` : ''}
                </Typography>
              </Box>
              {h.current_hotel_id ? (
                <Button
                  onClick={() => navigate(`${catalogAdminPath}/hotels/${encodeURIComponent(h.current_hotel_id!)}`)}
                >
                  {t('hotelDiscovery.openHotel')}
                </Button>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('hotelDiscovery.unlinked')}
                </Typography>
              )}
            </Stack>
          ))}
        </Stack>
        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <Button disabled={offset === 0 || hotels.isFetching} onClick={() => setOffset((v) => Math.max(0, v - 25))}>
            {t('hotelCatalogAdmin.previous')}
          </Button>
          <Button
            disabled={!hotels.data?.meta.has_more || hotels.isFetching || offset >= 10000000}
            onClick={() => setOffset((v) => v + 25)}
          >
            {t('hotelCatalogAdmin.next')}
          </Button>
        </Stack>
      </CatalogAdoption>
    </Paper>
  );
}
